import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { FormDialog } from './FormDialog';

export interface ConfirmDialogProps {
  open: boolean;
  /** Names the object: "Delete Lobby schedule?", "Remove Jane Doe?". */
  title: string;
  /** The consequence, in plain words. */
  children: React.ReactNode;
  /** The action, never "OK": "Delete", "Remove". The dialog closes once it succeeds. */
  confirmLabel: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  /** Destructive styling (default true). */
  destructive?: boolean;
  /** For irreversible deletes: the user must type this name to confirm. */
  typeToConfirm?: string;
  describeError?: (err: unknown) => string;
  confirmTestId?: string;
  testId?: string;
}

/** Confirmation for consequential actions (web-frontend.md, Modals). */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  onConfirm,
  onClose,
  destructive = true,
  typeToConfirm,
  describeError,
  confirmTestId,
  testId,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const close = () => {
    setTyped('');
    onClose();
  };
  return (
    <FormDialog
      open={open}
      title={title}
      submitLabel={confirmLabel}
      destructive={destructive}
      maxWidth="xs"
      canSubmit={!typeToConfirm || typed === typeToConfirm}
      onSubmit={async () => {
        await onConfirm();
        close();
      }}
      onClose={close}
      describeError={describeError}
      submitTestId={confirmTestId}
      testId={testId}
    >
      <Typography component="div">{children}</Typography>
      {typeToConfirm && (
        <TextField
          label={`Type “${typeToConfirm}” to confirm`}
          value={typed}
          autoComplete="off"
          onChange={(e) => {
            setTyped(e.target.value);
          }}
          slotProps={{ htmlInput: { 'data-testid': 'type-to-confirm' } }}
        />
      )}
    </FormDialog>
  );
}
